// Pure helpers + types for KPI form state. `higherIsBetter` is the
// interesting one: schema is `boolean?`, form models as
// `boolean | null` — `null` means indeterminate (allowed on Edit for
// legacy KPIs tolerate null; Create form coerces required).
// kpiNumberInput lets the user optionally override the server-assigned
// number on Create. Edit mode shows it read-only (immutable post-create).

import type {
  CreateKpiInput,
  EvidenceLink,
  Kpi,
  KpiOwnership,
  KpiValueType,
  RecurrenceFrequency,
} from "../../../../types";

/**
 * Indeterminate value (`null`) is tolerated on Edit for legacy KPIs
 * that pre-date the higherIsBetter field. The Definitions tab surfaces
 * a "Set direction" CTA on such rows so they can be resolved.
 */
export interface KpiFormState {
  name: string;
  definition: string;
  /** Value type picker. Default `percentage` matches legacy
   *  behaviour. Boolean short-circuits the target input to "Always Met"
   *  and hides the unit + amber-band fields. */
  valueType: KpiValueType;
  /** Free-text unit label (e.g. "hrs", "incidents", "$").
   *  Only surfaced when `valueType === "number"`. Empty on percentage /
   *  boolean KPIs and stripped on submit. */
  unit: string;
  target: string;
  measurementFrequency: RecurrenceFrequency | "";
  reportingFrequency: RecurrenceFrequency | "";
  reportDueOffset: number | "";
  isSla: boolean;
  serviceCreditFormula: string;
  /** Clause reference for the KPI. Renamed from `slaClauseReference` in
   *  Most KPIs reference a clause regardless of whether
   *  service credits apply, so this field is now always visible above
   *  the "Service credits apply" tickbox. Backend accepts both names on
   *  write (see `kpiClauseReference` Zod extension); on read the new
   *  name wins when both are present. */
  kpiClauseReference: string;
  higherIsBetter: boolean | null;
  evidenceLinks: EvidenceLink[];
  notes: string;
  /** Ownership flag for scorecard meta line. `""` means
   *  "not specified"; mapped to `null` on submit. */
  ownership: KpiOwnership | "";
  /** User-facing percent (1-50 on input); stored decimal
   *  (0.01-0.50) on the wire. `""` falls back to `null` on submit, which
   *  falls back to `AMBER_BAND_RELATIVE` (0.05) at runtime. */
  amberBandPercentInput: string;
  /** C1 — user-visible reference number as a string input. `""` means
   *  "let the server assign". Coerced to integer on submit. Read-only on
   *  Edit (shown in the modal header, not editable). */
  kpiNumberInput: string;
}

export type KpiFieldErrors = Partial<Record<keyof KpiFormState | "form", string>>;

export function buildEmptyKpiFormState(): KpiFormState {
  return {
    name: "",
    definition: "",
    valueType: "percentage",
    unit: "",
    target: "",
    measurementFrequency: "",
    reportingFrequency: "",
    reportDueOffset: "",
    isSla: false,
    serviceCreditFormula: "",
    kpiClauseReference: "",
    higherIsBetter: null,
    evidenceLinks: [],
    notes: "",
    ownership: "",
    amberBandPercentInput: "",
    kpiNumberInput: "",
  };
}

export function stateFromKpi(item: Kpi): KpiFormState {
  // valueType / clause-ref back-compat: undefined legacy KPIs default to
  // "percentage". `kpiClauseReference` reads the new field if present,
  // falling back to `slaClauseReference` so pre-PR-B KPIs keep their
  // reference visible in the form.
  const valueType: KpiValueType = item.valueType ?? "percentage";
  return {
    name: item.name,
    definition: item.definition,
    valueType,
    unit: valueType === "number" ? (item.unit ?? "") : "",
    target: item.target,
    measurementFrequency: (item.measurementFrequency as RecurrenceFrequency) ?? "",
    reportingFrequency: (item.reportingFrequency as RecurrenceFrequency) ?? "",
    reportDueOffset:
      typeof item.reportDueOffset === "number" ? item.reportDueOffset : "",
    isSla: !!item.isSla,
    serviceCreditFormula: item.serviceCreditFormula ?? "",
    kpiClauseReference:
      item.kpiClauseReference ?? item.slaClauseReference ?? "",
    higherIsBetter:
      typeof item.higherIsBetter === "boolean" ? item.higherIsBetter : null,
    evidenceLinks: item.evidenceLinks ? [...item.evidenceLinks] : [],
    notes: item.notes ?? "",
    ownership: item.ownership ?? "",
    amberBandPercentInput:
      typeof item.amberBandPercent === "number"
        ? // Convert decimal (e.g. 0.10) to display percent (e.g. "10")
          // without trailing zeros. `String(n * 100)` is fine for
          // 0.01..0.50 since those round cleanly to 1..50.
          String(Math.round(item.amberBandPercent * 1000) / 10)
        : "",
    kpiNumberInput: String(item.kpiNumber),
  };
}

/**
 * Frequency comparability — reporting must be equal-or-coarser than
 * measurement. Lower rank = more frequent.
 */
export const FREQUENCY_RANK: Record<RecurrenceFrequency, number> = {
  daily: 0,
  weekly: 1,
  monthly: 2,
  quarterly: 3,
  annually: 4,
};

/**
 * Build the backend Create payload. `kpiNumber` is included only when
 * `kpiNumberInput` is a valid integer ≥1; otherwise omitted so the
 * server assigns. Strips empty-string optionals. Coerces empty-string
 * frequency to a validation error upstream — this helper assumes
 * validate() has already caught that case.
 */
export function toCreateKpiInput(
  state: KpiFormState,
  contractId: string,
): CreateKpiInput {
  if (state.measurementFrequency === "" || state.reportingFrequency === "") {
    throw new Error(
      "toCreateKpiInput called with empty measurement or reporting frequency — validator should have blocked this",
    );
  }
  const parsedKpiNumber = parseInt(state.kpiNumberInput, 10);
  const base: CreateKpiInput = {
    contractId,
    name: state.name.trim(),
    ...(Number.isFinite(parsedKpiNumber) && parsedKpiNumber >= 1
      ? { kpiNumber: parsedKpiNumber }
      : {}),
    definition: state.definition.trim(),
    target: state.target.trim(),
    measurementFrequency: state.measurementFrequency,
    reportingFrequency: state.reportingFrequency,
    valueType: state.valueType,
  };
  // Unit only persists for number KPIs. Percentage and boolean KPIs
  // explicitly null it on submit so switching type clears a stale unit.
  if (state.valueType === "number" && state.unit.trim()) {
    base.unit = state.unit.trim();
  } else {
    base.unit = null;
  }
  if (state.reportDueOffset !== "") {
    base.reportDueOffset = state.reportDueOffset;
  }
  // Clause reference is always-visible — emit on the new
  // `kpiClauseReference` field. Backend still accepts `slaClauseReference`
  // for back-compat but prefers the new name on read.
  if (state.kpiClauseReference.trim()) {
    base.kpiClauseReference = state.kpiClauseReference.trim();
  }
  if (state.isSla) {
    base.isSla = true;
    if (state.serviceCreditFormula.trim()) {
      base.serviceCreditFormula = state.serviceCreditFormula.trim();
    }
  }
  if (state.higherIsBetter !== null) {
    base.isSla = base.isSla ?? undefined;
    base.higherIsBetter = state.higherIsBetter;
  }
  if (state.evidenceLinks.length > 0) base.evidenceLinks = state.evidenceLinks;
  if (state.notes.trim()) base.notes = state.notes.trim();
  // Ownership + amber-band override. Both absent/blank → null so
  // the backend clears any previously-set value on Edit.
  base.ownership = state.ownership === "" ? null : state.ownership;
  if (state.amberBandPercentInput.trim() === "") {
    base.amberBandPercent = null;
  } else {
    // Input is a user-facing percent (e.g. "10"); persist decimal.
    // Validator has already guaranteed 1..50 inclusive; the divide keeps
    // us inside the backend's 0 < x <= 0.5 Zod guard.
    const n = Number(state.amberBandPercentInput);
    base.amberBandPercent = Number.isFinite(n) ? n / 100 : null;
  }
  return base;
}
